import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import eventBus from "./eventBus.js";
import { EventName, combinePath, createEvent, createToken, getConditionKey, getEventName, isResultType, mountEvent } from "./utils.js";
import { checkLiteral, validate } from "./validate.js";
const useMemoFn = (fn)=>{
    const methodRef = useRef(fn);
    methodRef.current = fn;
    return useMemo(()=>({
            get current () {
                return methodRef.current;
            }
        }), []);
};
function useEventChat(name, ops) {
    const eventName = useMemo(()=>getEventName(name), [
        name
    ]);
    const id = useId();
    const token = useMemo(()=>createToken(getConditionKey(eventName, id, ops?.type)), [
        eventName,
        id,
        ops?.type
    ]);
    const nameRc = useMemoFn(name);
    const options = useMemoFn(ops);
    const tokenRc = useMemoFn(token);
    const errorHandle = useCallback((error, data)=>{
        const { debug } = options.current ?? {};
        if (error instanceof Error && debug) {
            const cause = isResultType(error.cause) ? error.cause : void 0;
            debug?.({
                ...cause,
                status: 'invalid',
                data
            });
        }
    }, [
        options
    ]);
    const getResult = useCallback(()=>{
        const { group, debug } = options.current ?? {};
        return [
            {
                origin: nameRc.current,
                time: new Date(),
                group,
                id
            },
            debug
        ];
    }, [
        id,
        nameRc,
        options
    ]);
    const callbackHandle = useCallback((data)=>{
        const opitem = options.current;
        const upRecord = {
            ...data,
            name: nameRc.current,
            type: opitem?.type
        };
        if (!opitem) return;
        const throwError = (error)=>errorHandle(error, data);
        if (void 0 !== opitem.schema) validate(upRecord, {
            ...opitem,
            schema: opitem.schema
        }, tokenRc.current).then(opitem.callback).catch(throwError);
        else checkLiteral(upRecord, {
            ...opitem,
            schema: void 0
        }, tokenRc.current).then(opitem.callback).catch(throwError);
    }, [
        nameRc,
        options,
        tokenRc,
        errorHandle
    ]);
    const emit = useCallback((detail)=>{
        const [data, debug] = getResult();
        const current = {
            ...detail,
            ...data,
            originName: detail.name,
            rule: ''
        };
        try {
            current.rule = combinePath(detail.name, data.origin);
            const event = createEvent(current);
            debug?.({
                data: event.detail,
                status: 'emit'
            });
            document.body.dispatchEvent(event);
        } catch (error) {
            debug?.({
                data: current,
                lost: error,
                status: 'lost'
            });
        }
    }, [
        getResult
    ]);
    useEffect(()=>{
        if (!document.body.dataset.globalIsListened) {
            document.body.addEventListener(EventName, mountEvent);
            document.body.dataset.globalIsListened = '1';
        }
    }, []);
    useEffect(()=>{
        const [data, debug] = getResult();
        debug?.({
            data: {
                ...data,
                name: origin
            },
            status: 'init'
        });
    }, [
        getResult
    ]);
    useEffect(()=>{
        eventBus.on(eventName, callbackHandle);
        return ()=>{
            eventBus.off(eventName, callbackHandle);
        };
    }, [
        eventName,
        callbackHandle
    ]);
    return Object.freeze({
        token,
        emit
    });
}
export { useEventChat, useMemoFn };
